import { Component, inject } from '@angular/core';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { Subject } from 'rxjs';
import { IContact } from '../../../dashboards/interfaces/tiles/definition-helpers/contact.interface';
import { IContactsTileInstance } from '../../../dashboards/interfaces/tiles/definitions/contacts-tile-definition.interface';
import { CONTENT_ALIGNMENT_OPTIONS } from '../../constants/content-alignment-options.constant';
import { HORIZONTAL_POSITION_OPTIONS } from '../../constants/horizontal-position-options.constant';
import { VERTICAL_POSITION_OPTIONS } from '../../constants/vertical-position-options.constant';
import { ISelectOption } from '../../interfaces/select-option.interface';
import { TileCreatorHelpersService } from '../../services/tile-creator-helpers.service';
import { TileCreatorService } from '../../services/tile-creator.service';
import { TILE_REQUEST } from '../../tokens/tile-request.token';
import { TileSetupWizardComponent } from '../tile-setup-wizard.component';

@Component({
  standalone: false,
  selector: 'lib-contacts-setup-wizard',
  templateUrl: './contacts-setup-wizard.component.html',
  styleUrls: ['../shared-tile-creator-styles.scss', './contacts-setup-wizard.component.scss'],
})
export class ContactsSetupWizardComponent extends TileSetupWizardComponent<IContactsTileInstance> {
  get contactsControl(): FormArray<FormGroup> {
    return this.setupForm.get('contacts') as FormArray<FormGroup>;
  }

  contentAlignmentOptions: ISelectOption[];
  positionsOptions: ISelectOption[];
  horizontalPositionOptions: ISelectOption[];
  fontSizesOptions: string[];

  private destroy$: Subject<void> = new Subject<void>();

  constructor(
    private readonly fb: FormBuilder,
    private readonly tileCreatorHelpersService: TileCreatorHelpersService,
    private readonly tileCreatorService: TileCreatorService,
  ) {
    super();

    this.tileInstance = inject(TILE_REQUEST).tileInstance;
  }

  ngOnInit() {
    this.initSelectOptions();
    this.initSetupForms();

    this.hookForm();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  initSelectOptions() {
    this.contentAlignmentOptions = CONTENT_ALIGNMENT_OPTIONS;
    this.positionsOptions = VERTICAL_POSITION_OPTIONS;
    this.horizontalPositionOptions = HORIZONTAL_POSITION_OPTIONS;
    this.fontSizesOptions = this.tileCreatorHelpersService.generateFontSize();
  }

  initSetupForms() {
    this.setupForm = this.fb.group({
      contacts: this.fb.array([]),
      background: [''],
    });

    if (this.tileInstance) {
      this.setupForm.patchValue(this.tileInstance);

      this.tileInstance.contacts?.forEach((contact: IContact) => {
        this.addContact(contact);
      });
    } else {
      this.addContact();
    }
  }

  addContact(contact?: IContact) {
    const contactFormGroup = this.fb.group({
      fullName: [''],
      fullNameFontSize: ['6px'],
      fullNameFontColor: ['#000000'],
      role: [''],
      roleFontSize: ['6px'],
      roleFontColor: ['#000000'],
      text: [''],
      textFontSize: ['6px'],
      textFontColor: ['#000000'],
      phone: [''],
      email: [''],
      img: [''],
      imgPosition: ['start'],
    });

    contactFormGroup.patchValue(contact);

    this.contactsControl.push(contactFormGroup);
  }

  removeContact(contactIndex: number) {
    this.contactsControl.removeAt(contactIndex);
  }

  onCreate() {
    Object.assign(this.tileInstance, this.getChanges());
    this.tileCreatorService.onChange.next();
  }

  getChanges() {
    return {
      tileType: 'contacts',
      canView: 'true',
      ...this.setupForm.value,
    };
  }
}
